/**
 * Soft I1a: sticky compact chronological strip on the input screens.
 *
 * One quiet line that IS the input view: hero seat code + hole cards, the
 * past actions in order (same seat codes and words as the share line — never
 * 「나」 / Hero / icons), the current street's board, and on the Line step the
 * pot in bb (input screen only; 「?」 when any part is not an entered fact).
 *
 * Overflow (poker lock): hole cards and the whole board (flop, turn, river)
 * always stay; only past actions are elided, oldest first, into 「…」 —
 * never a left cut. If it still does not fit, board items drop the street
 * word (cards only, e.g. `Q♥7♣2♦`), never a card. The
 * fit runs on the real DOM after mount (`fitInputStrips`): one line when the
 * strip is ≥ 300px wide (390px phone), at most two lines below that
 * (175% zoom). No horizontal scroll, no clipping.
 *
 * Tap-rewind (Line step): tapping a past action / board dims it and
 * everything after it and shows ONE inline 「여기부터 다시」 confirm (+ cancel)
 * inside the strip. Nothing is removed until confirm (state.confirmRewind);
 * the share step is blocked while dimmed items exist, so they never reach
 * share / Copy / PNG / `#/h/`. Never part of those outputs itself.
 */
import { t } from '../i18n/strings'
import { formatBb } from '../lib/handFlow'
import { inputPot } from './inputPot'
import { lineCard } from '../lib/lineText'
import { handLineSegments } from '../lib/poker'
import type { HandState } from '../lib/types'
import {
  beginRewind,
  cancelRewind,
  confirmRewind,
  getRewindPending,
  getState,
  isHeroSeatPicked,
  type RewindTarget,
} from '../state'
import { el } from './dom'

export type StripItem = {
  key: string
  kind: 'hero' | 'action' | 'board'
  text: string
  /** Never elided (hole cards, every board card flop→river, a pending rewind target and all after it). */
  keep: boolean
  /** Board only: the cards without the street word — last-resort compact form (all cards stay). */
  cards?: string
  target: RewindTarget | null
}

export type StripCell =
  | { kind: 'item'; item: StripItem; dim: boolean }
  | { kind: 'ellipsis'; keys: string[]; dim: boolean }

export function stripTargetKey(target: RewindTarget | null): string | null {
  if (!target) return null
  return 'actionId' in target ? `a:${target.actionId}` : `b:${target.street}`
}

/** Chronological items (no pot) — the same segments the share line uses. */
export function buildStripItems(hand: HandState, opts: { seatPicked: boolean; pending?: RewindTarget | null }): StripItem[] {
  const segs = handLineSegments(hand, { seatPicked: opts.seatPicked })
  const pendingKey = stripTargetKey(opts.pending ?? null)
  // While a rewind is pending, the tapped item and everything after it (what
  // 「여기부터 다시」 would remove) are never elided: confirm is only pressable
  // while all of it is visible.
  const keyOf = (s: (typeof segs)[number]) =>
    s.kind === 'hero' ? 'hero' : s.kind === 'action' ? `a:${s.actionId}` : `b:${s.street}`
  const fromIdx = pendingKey ? segs.findIndex((s) => keyOf(s) === pendingKey) : -1
  return segs.map((s, i) => {
    if (s.kind === 'hero') return { key: 'hero', kind: 'hero', text: s.text, keep: true, target: null }
    const target: RewindTarget = s.kind === 'action' ? { actionId: s.actionId } : { street: s.street }
    const key = stripTargetKey(target)!
    const cards =
      s.kind === 'board'
        ? (s.street === 'flop' ? hand.board.flop : [hand.board[s.street]])
            .filter((c): c is NonNullable<typeof c> => Boolean(c))
            .map(lineCard)
            .join('')
        : undefined
    return {
      cards, key, kind: s.kind, text: s.text, keep: s.kind === 'board' || (fromIdx >= 0 && i >= fromIdx), target }
  })
}

/** Number of items that may be elided (everything not kept). */
export function elidableCount(items: StripItem[]): number {
  return items.filter((i) => !i.keep).length
}

/**
 * Elide the `drop` oldest elidable items; consecutive elided items become one
 * 「…」 cell. Kept items never move or disappear. Items at/after the pending
 * rewind target are marked dim.
 */
export function elideStrip(items: StripItem[], drop: number, pending: RewindTarget | null = null): StripCell[] {
  const pendingKey = stripTargetKey(pending)
  const dimFrom = pendingKey ? items.findIndex((i) => i.key === pendingKey) : -1
  const dimAt = (idx: number) => dimFrom >= 0 && idx >= dimFrom
  let left = Math.max(0, drop)
  const cells: StripCell[] = []
  items.forEach((item, idx) => {
    if (!item.keep && left > 0) {
      left--
      const prev = cells[cells.length - 1]
      if (prev && prev.kind === 'ellipsis') {
        prev.keys.push(item.key)
        prev.dim = prev.dim && dimAt(idx)
      } else cells.push({ kind: 'ellipsis', keys: [item.key], dim: dimAt(idx) })
      return
    }
    cells.push({ kind: 'item', item, dim: dimAt(idx) })
  })
  return cells
}

/** Input-only pot text: `팟 6bb` / `팟 ?` (existing tableVizPotValue string). */
export function stripPotText(hand: HandState): string {
  const pot = inputPot(hand)
  return t().tableVizPotValue(pot.kind === 'known' ? formatBb(pot.bb) : '?')
}

function renderCells(cells: StripCell[], tappable: boolean, pendingKey: string | null, compact = false): HTMLElement[] {
  const kids: HTMLElement[] = []
  cells.forEach((cell, i) => {
    if (i > 0) kids.push(el('span', { className: 'inputStripSep', text: '·', attrs: { 'aria-hidden': 'true' } }))
    if (cell.kind === 'ellipsis') {
      kids.push(
        el('span', {
          className: ['inputStripItem', 'inputStripEllipsis', cell.dim ? 'inputStripDim' : ''].filter(Boolean).join(' '),
          text: '…',
          attrs: { 'data-strip-elided': cell.keys.join(',') },
        }),
      )
      return
    }
    const { item } = cell
    const from = pendingKey != null && item.key === pendingKey
    const cls = ['inputStripItem', cell.dim ? 'inputStripDim' : '', from ? 'inputStripFrom' : ''].filter(Boolean).join(' ')
    // Soft I1b: aria-current only on the tapped item while the rewind is pending
    // (re-render on cancel drops it). No visual change.
    const attrs = { 'data-strip-key': item.key, 'data-strip-kind': item.kind, 'aria-current': from ? 'true' : undefined }
    const text = compact && item.kind === 'board' && item.cards ? item.cards : item.text
    if (tappable && item.target && !cell.dim) {
      const target = item.target
      kids.push(
        el('button', {
          className: `${cls} inputStripTap`,
          attrs: { type: 'button', ...attrs, 'aria-label': item.text },
          text,
          on: { click: () => beginRewind(target) },
        }),
      )
      return
    }
    kids.push(
      el('span', {
        className: cls,
        attrs: { ...attrs, 'aria-disabled': cell.dim ? 'true' : undefined, 'aria-label': text !== item.text ? item.text : undefined },
        text,
      }),
    )
  })
  return kids
}

type StripModel = { items: StripItem[]; pending: RewindTarget | null; tappable: boolean; pot: string | null }
const models = new WeakMap<HTMLElement, StripModel>()

function fillLine(line: HTMLElement, model: StripModel, drop: number, compact = false): void {
  const cells = elideStrip(model.items, drop, model.pending)
  const kids = renderCells(cells, model.tappable, stripTargetKey(model.pending), compact)
  if (model.pot) {
    if (kids.length) kids.push(el('span', { className: 'inputStripSep', text: '·', attrs: { 'aria-hidden': 'true' } }))
    kids.push(el('span', { className: 'inputStripItem inputStripPot', attrs: { 'data-strip-pot': '' }, text: model.pot }))
  }
  while (line.firstChild) line.removeChild(line.firstChild)
  line.append(...kids)
  line.setAttribute('data-strip-drop', String(drop))
  if (compact) line.setAttribute('data-strip-compact', '')
  else line.removeAttribute('data-strip-compact')
}

/** Test seam: re-fill a rendered strip line at a given elision / compact level. */
export function refillStripLine(line: HTMLElement, drop: number, compact = false): boolean {
  const model = models.get(line)
  if (!model) return false
  fillLine(line, model, drop, compact)
  return true
}

/**
 * The sticky strip for the current input step, or null (setup / summary /
 * before the seat is picked).
 */
export function renderInputStrip(): HTMLElement | null {
  const { step, hand } = getState()
  if (step !== 'cards' && step !== 'actions' && step !== 'board') return null
  const ui = t()
  const pending = getRewindPending()
  const items = buildStripItems(hand, { seatPicked: isHeroSeatPicked(), pending })
  const onLine = step === 'actions' || step === 'board'
  const model: StripModel = {
    items,
    pending,
    tappable: onLine,
    pot: onLine ? stripPotText(hand) : null,
  }
  if (!items.length && !model.pot) return null
  const line = el('p', {
    className: 'inputStripLine',
    attrs: { role: 'status', 'aria-label': ui.lineStripAria },
  })
  models.set(line, model)
  fillLine(line, model, 0)
  const confirm = pending
    ? el('div', { className: 'inputStripConfirm', attrs: { 'data-strip-confirm': '' } }, [
        el('button', {
          className: 'inputStripConfirmBtn',
          attrs: { type: 'button', 'data-strip-rewind-confirm': '' },
          text: ui.flowRewind,
          on: { click: () => void confirmRewind() },
        }),
        el('button', {
          className: 'inputStripCancelBtn',
          attrs: { type: 'button', 'data-strip-rewind-cancel': '' },
          text: ui.flowRewindCancel,
          on: { click: () => cancelRewind() },
        }),
      ])
    : null
  return el('div', { className: 'inputStrip', attrs: { 'data-soft-i1a': 'strip' } }, [line, confirm])
}

/**
 * After mount: elide oldest past items until the line fits (1 line when the
 * strip is ≥ 300px wide, else ≤ 2 lines). Kept items are never elided; if
 * even then it does not fit, it wraps (never clipped / scrolled).
 */
export function fitInputStrips(root: ParentNode = document): void {
  const lines = root.querySelectorAll<HTMLElement>('.inputStripLine')
  const setPad = () => {
    const strip = root.querySelector<HTMLElement>('.inputStrip')
    // The confirm row overlays below the strip: reserve it too while shown.
    const confirm = strip?.querySelector<HTMLElement>('.inputStripConfirm')
    const h = strip
      ? Math.ceil(strip.getBoundingClientRect().height + (confirm ? confirm.getBoundingClientRect().height : 0)) + 8
      : 0
    document.documentElement.style.setProperty('--input-strip-h', `${h}px`)
  }
  lines.forEach((line) => {
    const model = models.get(line)
    if (!model) return
    const max = elidableCount(model.items)
    fillLine(line, model, 0)
    const lh = parseFloat(getComputedStyle(line).lineHeight) || 18
    const maxLines = line.clientWidth >= 300 ? 1 : 2
    const fits = () => line.scrollHeight <= lh * maxLines + lh * 0.5
    let drop = 0
    while (!fits() && drop < max) {
      drop++
      fillLine(line, model, drop)
    }
    // Last resort: board items show their cards only (street word moves to
    // aria-label). Every card stays visible; nothing else is cut.
    if (!fits()) fillLine(line, model, drop, true)
  })
  setPad()
}
